import { apiSlice } from "./baseApiSlice";
import { GenericPaginatedApi } from "../../types";
import {
  AccountingStatement,
  CreateEmittedTitleDocumentParams,
  CreateEmittedTitleDocumentResponse,
  CreateStatementDocumentParams,
  CreateStatementDocumentResponse,
  CreditDebitStatus,
  Document,
  DocumentS3UploadParams,
  DownloadAccountingStatementTitleParams,
  EmittedTitle,
  EmittedTitleDocument,
  GetAccountingStatementsListParams,
  GetEmittedTitlesListParams,
  GetInboundRemittanceApiParams,
  GetLinkedEmittedTitlesListResponse,
  GetReconciliationGroupsApiParams,
  GetStatementTitleListParams,
  InboundRemittance,
  PaginatedApiParams,
  PatchDocumentParams,
  ReconciliationGroup,
  StatementTitle,
  StatementTitleDownloadParams,
  StatementTitleRelatedNegotiation
} from "../../types/reconciliation";
import handleDownloadFile from "../../utils/handleDownloadFile";

export const reconciliationsApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
      getAccountingStatementsList: builder.query<GenericPaginatedApi<AccountingStatement>, GetAccountingStatementsListParams & PaginatedApiParams>({
        query: (queryArgs) => ({
          url: "/negotiation/accounting_statement/",
          params: queryArgs
        }),
        providesTags: ["AccountingStatements"]
      }),
      getStatementTitlesList: builder.query<GenericPaginatedApi<StatementTitle>, GetStatementTitleListParams & PaginatedApiParams>({
        query: (queryArgs) => ({
          url: "/negotiation/statement_title/",
          params: queryArgs
        }),
        providesTags: (result) =>
          result ? result.results.map(({ uuid }) => ({ type: "StatementTitles", id: uuid })) : [{
            type: "StatementTitles",
            id: "LIST"
          }]
      }),
      patchStatementTitle: builder.mutation <StatementTitle & {
        paramsToUpdateCache: GetStatementTitleListParams & PaginatedApiParams
      }, Partial<StatementTitle>>({
        query: ({ uuid, ...body }) => ({
          url: `/negotiation/statement_title/${uuid}/`,
          method: "PATCH",
          body
        }),
        invalidatesTags: ["StatementTitles", "ReconciliationGroups"]
      }),
      getLinkedEmittedTitles:
        builder.query<GetLinkedEmittedTitlesListResponse, { uuid: string } & PaginatedApiParams>({
          query: ({ uuid, ...paginationParams }) => ({
            url: `/negotiation/statement_title_group/${uuid}/get_linked_emitted_titles/`,
            params: paginationParams
          })
          //providesTags: ["StatementTitlesGroups"]
        }),
      getStatementTitleGroupsStatementTitles:
        builder.query<GenericPaginatedApi<StatementTitle>, { uuid: string } & PaginatedApiParams>({
          query: ({ uuid, ...paginationParams }) => ({
            url: `/negotiation/statement_title_group/${uuid}/statement_titles/`,
            params: paginationParams
          })
          //providesTags: ["StatementTitlesGroups"]
        }),
      getStatementTitleGroupRelatedNegotiations:
        builder.query<StatementTitleRelatedNegotiation[], { uuid: string }>({
          query: ({ uuid, ...paginationParams }) => ({
            url: `/negotiation/statement_title_group/${uuid}/get_related_negotiation/`
          })
          //providesTags: ["StatementTitlesGroups"]
        }),
      createStatementDocument:
        builder.mutation<CreateStatementDocumentResponse, CreateStatementDocumentParams>({
          query: (queryArgs) => ({
            method: "POST",
            url: "negotiation/statement_document/",
            body: queryArgs
          }),
          invalidatesTags: ["AccountingStatements"]
        }),
      patchStatementDocument:
        builder.mutation<Document, PatchDocumentParams>({
          query: ({ uuid, ...queryArgs }) => ({
            method: "PATCH",
            url: `negotiation/statement_document/${uuid}/`,
            body: queryArgs
          }),
          invalidatesTags: ["StatementTitles", "ReconciliationGroups"]
        }),
      deleteStatementDocument:
        builder.mutation<void, string>({
          query: (uuid) => ({
            method: "DELETE",
            url: `negotiation/statement_document/${uuid}/`
          }),
          invalidatesTags: ["StatementTitles", "AccountingStatements", "ReconciliationGroups"]
        }),
      deleteAccountingStatement:
        builder.mutation<void, string>({
          query: (uuid) => ({
            method: "DELETE",
            url: `negotiation/accounting_statement/${uuid}/`
          }),
          invalidatesTags: ["AccountingStatements", "ReconciliationGroups"]
        }),
      deleteStatementTitle:
        builder.mutation<void, string>({
          query: (uuid) => ({
            method: "DELETE",
            url: `negotiation/statement_title/${uuid}/`
          }),
          invalidatesTags: ["StatementTitles"]
        }),
      uploadDocumentToS3Bucker:
        builder.mutation<unknown, { file: File } & DocumentS3UploadParams>({
          query: ({ file, url, fields }) => {
            const formData = new FormData();
            Object.entries(fields).forEach(([key, value]) => {
              formData.append(key, value);
            });
            formData.append("file", file);
            return {
              url: url,
              method: "POST",
              multipart: true,
              body: formData
            };
          }
        }),
      downloadAccountingStatementTitles:
        builder.query<void, DownloadAccountingStatementTitleParams>({
          query: ({ uuid, ...queryArgs }) => ({
            url: `negotiation/accounting_statement/${uuid}/download_titles/`,
            params: queryArgs,
            responseHandler: (response) => {
              return handleDownloadFile(response);
            },
            cache: "no-cache"
          })
        }),
      downloadAccountingStatementReconciliationReport:
        builder.query<void, string>({
          query: (uuid) => ({
            url: `negotiation/accounting_statement/${uuid}/reconciliation_report/`,
            responseHandler: (response) => {
              return handleDownloadFile(response);
            },
            cache: "no-cache"
          })
        }),
      downloadInBoundRemittances:
        builder.query<void, string>({
          query: (uuid) => ({
            url: `negotiation/accounting_statement/${uuid}/download_inbound_remittances/`,
            responseHandler: (response) => {
              return handleDownloadFile(response);
            },
            cache: "no-cache"
          })
        }),
      downloadStatementTitlesDocument:
        builder.query<void, StatementTitleDownloadParams>({
          query: (queryArgs) => ({
            url: `negotiation/statement_title/download/`,
            params: queryArgs,
            responseHandler: (response) => {
              return handleDownloadFile(response);
            },
            cache: "no-cache"
          })
        }),
      getAccountingStatementDocumentUrl:
        builder.query<{ name: string, url: string }, string>({
          query: (uuid) => ({
            url: `negotiation/accounting_statement/${uuid}/download_document/`
          })
        }),
      reconcileAccountingStatement:
        builder.mutation<void, string>({
          query: (uuid) => ({
            method: "POST",
            url: `negotiation/accounting_statement/${uuid}/reconcile/`
          }),
          invalidatesTags: ["AccountingStatements"]
        }),
      getEmittedTitlesList:
        builder.query<GenericPaginatedApi<EmittedTitle>, GetEmittedTitlesListParams & PaginatedApiParams>({
          query: (queryArgs) => ({
            url: "/negotiation/emitted_title/",
            params: queryArgs
          }),
          providesTags: (result) =>
            result ? result.results.map(({ uuid }) => ({ type: "EmittedTitles", id: uuid })) : [{
              type: "EmittedTitles",
              id: "LIST"
            }]
        }),
      getEmittedTitleDocuments:
        builder.query<GenericPaginatedApi<EmittedTitleDocument>, PaginatedApiParams>({
          query: (queryArgs) => ({
            url: "/negotiation/emitted_titles_backlog/",
            params: queryArgs
          }),
          providesTags: (result) => [{
            type: "EmittedTitlesDocuments",
            id: "LIST"
          }]
        }),
      createEmittedTitleDocument:
        builder.mutation<CreateEmittedTitleDocumentResponse, CreateEmittedTitleDocumentParams>({
          query: (queryArgs) => ({
            method: "POST",
            url: "negotiation/emitted_titles_document/",
            body: queryArgs
          }),
          invalidatesTags: ["EmittedTitlesDocuments"]
        }),
      patchEmittedTitleDocument:
        builder.mutation<Document, PatchDocumentParams>({
          query: ({ uuid, ...queryArgs }) => ({
            method: "PATCH",
            url: `negotiation/emitted_titles_document/${uuid}/`,
            body: queryArgs
          }),
          invalidatesTags: ["StatementTitles"]
        }),
      deleteEmittedTitleDocument:
        builder.mutation<void, string>({
          query: (uuid) => ({
            method: "DELETE",
            url: `negotiation/emitted_titles_document/${uuid}/`
          }),
          invalidatesTags: ["EmittedTitlesDocuments"]
        }),
      confirmTitles: builder.mutation<void, { name?: string, notes?: string, statement_title_groups?: string[] }>({
        query: (args) => ({
          url: "negotiation/statement_title_group/confirm_titles/",
          method: "POST",
          body: args
        }),
        invalidatesTags: ["StatementTitles", "Remittances", "StatementTitlesGroups"]
      }),
      getInboundRemittance: builder.query<GenericPaginatedApi<InboundRemittance>, PaginatedApiParams & GetInboundRemittanceApiParams>({
        query: (params) => ({
          url: "negotiation/inbound_remittance/",
          params: params
        }),
        providesTags: ["Remittances"]
      }),
      downloadInboundRemittance: builder.query<void, string>({
        query: (uuid) => ({
          url: `negotiation/inbound_remittance/${uuid}/download/`,
          responseHandler: (response) => {
            return handleDownloadFile(response);
          },
          cache: "no-cache"
        })
      }),
      deleteInboundRemittance: builder.mutation<void, string>({
        query: (uuid) => ({
          url: `negotiation/inbound_remittance/${uuid}/`,
          method: "DELETE"
        }),
        invalidatesTags: ["Remittances"]
      }),
      getReconciliationGroups: builder.query<GenericPaginatedApi<ReconciliationGroup>, PaginatedApiParams & GetReconciliationGroupsApiParams>({
        query: (params) => (
          {
            url: "negotiation/reconciliation_group/",
            params: params
          }
        ),
        providesTags: ["ReconciliationGroups"]
      }),
      startGroupReconciliation: builder.mutation<any, string>({
        query: (uuid) => ({
          url: `negotiation/reconciliation_group/${uuid}/reconcile/`,
          method: "POST"
        }),
        invalidatesTags: [
          "ReconciliationGroups",
          "AccountingStatements",
          "StatementTitles",
          "EmittedTitlesDocuments",
          "EmittedTitles",
          "Remittances",
          "StatementTitlesGroups"
        ]
      }),
      stopGroupReconciliation: builder.mutation<any, string>({
        query: (uuid) => ({
          url: `negotiation/reconciliation_group/${uuid}/kill_reconciliation_process/`,
          method: "POST"
        }),
        invalidatesTags: ["ReconciliationGroups"]
      }),
      downloadReconciliationGroupTitlesDocument:
        builder.query<void, string>({
          query: (uuid) => ({
            url: `negotiation/reconciliation_group/${uuid}/download_titles/`,
            responseHandler: (response) => {
              return handleDownloadFile(response);
            },
            cache: "no-cache"
          })
        }),
      downloadReconciliationGroupReportDocument:
        builder.query<void, string>({
          query: (uuid) => ({
            url: `negotiation/reconciliation_group/${uuid}/reconciliation_report/`,
            responseHandler: (response) => {
              return handleDownloadFile(response);
            },
            cache: "no-cache"
          })
        }),
      getStatementTitleGroupsList: builder.query<GenericPaginatedApi<StatementTitle & {
        is_single: boolean
      }>, GetStatementTitleListParams & PaginatedApiParams>({
        query: (queryArgs) => ({
          url: "/negotiation/statement_title_group/",
          params: queryArgs
        }),
        providesTags: ["StatementTitlesGroups"]
      }),
      getConfirmableGroups: builder.query<StatementTitle[], { credit_debit_status: CreditDebitStatus }>({
        query: (queryArgs) => ({
          url: "negotiation/statement_title_group/get_confirmable_groups/",
          params: queryArgs
        })
      }),
      archiveTitles: builder.mutation<void, { statement_titles?: string[], name?: string, notes?: string }>({
        query: (args) => ({
          url: "negotiation/statement_title/archive_titles/",
          method: "POST",
          body: args
        }),
        invalidatesTags: ["StatementTitles", "Remittances"]
      }),
      patchRemittance: builder.mutation<void, {remittance_uuid: string} & Partial<InboundRemittance>>({
        query: ({ remittance_uuid, ...args }) => ({
          url: `negotiation/inbound_remittance/${remittance_uuid}/`,
          method: "PATCH",
          body: args
        }),
        invalidatesTags: ["Remittances"],
      })
    })
  })
;

export const {
  useGetAccountingStatementsListQuery,
  useGetStatementTitlesListQuery,
  useCreateStatementDocumentMutation,
  usePatchStatementDocumentMutation,
  useDeleteStatementDocumentMutation,
  useDeleteStatementTitleMutation,
  useUploadDocumentToS3BuckerMutation,
  useLazyDownloadAccountingStatementTitlesQuery,
  useLazyDownloadAccountingStatementReconciliationReportQuery,
  useLazyDownloadStatementTitlesDocumentQuery,
  useDeleteAccountingStatementMutation,
  useLazyGetAccountingStatementDocumentUrlQuery,
  useGetEmittedTitlesListQuery,
  useGetEmittedTitleDocumentsQuery,
  useCreateEmittedTitleDocumentMutation,
  usePatchEmittedTitleDocumentMutation,
  useDeleteEmittedTitleDocumentMutation,
  useReconcileAccountingStatementMutation,
  useGetLinkedEmittedTitlesQuery,
  usePatchStatementTitleMutation,
  useLazyDownloadInBoundRemittancesQuery,
  useConfirmTitlesMutation,
  useGetInboundRemittanceQuery,
  useLazyDownloadInboundRemittanceQuery,
  useDeleteInboundRemittanceMutation,
  useGetReconciliationGroupsQuery,
  useStartGroupReconciliationMutation,
  useStopGroupReconciliationMutation,
  useLazyDownloadReconciliationGroupTitlesDocumentQuery,
  useLazyDownloadReconciliationGroupReportDocumentQuery,
  useGetStatementTitleGroupsListQuery,
  useGetStatementTitleGroupsStatementTitlesQuery,
  useGetStatementTitleGroupRelatedNegotiationsQuery,
  useLazyGetConfirmableGroupsQuery,
  useArchiveTitlesMutation,
  usePatchRemittanceMutation,
} = reconciliationsApiSlice;